import { useRef, useState } from 'react';
import { FieldPath, FieldValues, UseControllerProps, useFormContext } from 'react-hook-form';
import { faHashtag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ContactFormValues } from 'assets/js/pages/contacts/types';
import * as Select from 'components/select';
import { SelectItemWithLabel } from 'components/select/types';
import { useSuspenseCurrentAccountTags } from 'customHooks/useCurrentAccountTags';

const tempCreateTag = 'TEMP_TAG';
interface UseFormContactTagsProps<T extends FieldValues, K extends FieldPath<T>>
    extends UseControllerProps<T, K> {
    allowNewTags?: boolean;
}

const UseFormContactTags = <T extends FieldValues, K extends FieldPath<T>>(
    props: UseFormContactTagsProps<T, K>,
) => {
    const { name, control, allowNewTags = true, rules } = props;
    const { data: accountContactTags } = useSuspenseCurrentAccountTags();
    const [inputValue, setInputValue] = useState('');
    const [inMemoryTags, setInMemoryTags] = useState<SelectItemWithLabel[]>([]);
    const tags: SelectItemWithLabel[] = [
        ...accountContactTags.map(tag => ({ id: String(tag.id), label: tag.name })),
        ...inMemoryTags,
    ];
    const ref = useRef<HTMLLIElement | null>(null);
    const showNewTagListItem =
        inputValue.length > 0 && tags.findIndex(tag => tag.label === inputValue) === -1;

    const { setValue, watch } = useFormContext<ContactFormValues>();
    const tagsValue = watch('tags');

    return (
        <Select.UseFormSearchableSelect
            name={name}
            control={control}
            multi={true}
            showTags={true}
            tagLeftAdornment={
                <FontAwesomeIcon icon={faHashtag} className="text-gray-400" size="xs" />
            }
            rules={rules}
            alwaysVisibleItemIds={[tempCreateTag]}
            onSelectItem={item => {
                if ((item as SelectItemWithLabel).id === tempCreateTag) {
                    const newTag = { id: inputValue, label: inputValue };
                    setInMemoryTags(tags => [...tags, newTag]);
                    setValue('tags', [...tagsValue, newTag]);
                }
                setInputValue('');
            }}
            inputProps={{
                value: inputValue,
                onChange: e => setInputValue(e.currentTarget.value),
                onKeyDown: e => {
                    const value = e.currentTarget.value;
                    if (e.key === 'Enter' && value.length > 0) {
                        e.preventDefault();
                        ref.current?.click();
                    }
                },
            }}
        >
            {allowNewTags && showNewTagListItem && (
                <Select.ListItem ref={ref} value={{ id: tempCreateTag, label: tempCreateTag }}>
                    <p className={'font-light'}>
                        Add <span className={'font-medium'}>{inputValue}</span> (press enter)
                    </p>
                </Select.ListItem>
            )}
            {tags.map((tag, index) => (
                <Select.ListItem key={`${tag.label}-${index}`} value={tag} checkable={true}>
                    {tag.label}
                </Select.ListItem>
            ))}
        </Select.UseFormSearchableSelect>
    );
};

export { UseFormContactTags };
export type { UseFormContactTagsProps };
